// src/components/BulkUploadDomainsModal.js
import React, { useState } from 'react';
import { Upload, X, AlertCircle, CheckCircle, FileText, Globe, Download } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const BulkUploadDomainsModal = ({ isOpen, onClose, onUploadComplete, activeCompany }) => {
  const { getToken } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.csv') && selectedFile.type !== 'text/csv') {
        setError('Please upload a CSV file (.csv)');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!activeCompany?.id) {
      setError('No active company selected');
      return;
    }

    setUploading(true);
    setError('');
    setResults(null);

    try {
      const token = await getToken();

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/companies/${activeCompany.id}/impersonating-domains/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setResults(data.results);

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `domain,type,status,risk,registered,registrar,similarity,ip_address,notes
glareindustriess.com,Typosquatting,Active,High,2026-01-15,Namecheap,95,185.234.219.45,Extra s at end - likely phishing
glare-industries.com,Combosquatting,Monitoring,Critical,2025-12-28,GoDaddy,98,192.168.45.23,Hyphenated version hosting phishing portal
g1areindustries.com,Homograph Attack,Active,Medium,2026-02-10,Tucows,88,,Homograph l to 1 substitution`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'impersonating_domains_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetAndClose = () => {
    setFile(null);
    setResults(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-cyan-500/30 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-cyan-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" />
                Bulk Upload Impersonating Domains
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Upload CSV file with impersonating domains for{' '}
                <span className="text-cyan-400 font-medium">{activeCompany?.name || 'selected company'}</span>
              </p>
            </div>
            <button
              onClick={resetAndClose}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all"
              disabled={uploading}
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">

          {/* Template Download */}
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-400">Need a template?</p>
                <p className="text-sm text-blue-300 mt-1">
                  Download our CSV template with the correct column headers and example data
                </p>
                <button
                  onClick={downloadTemplate}
                  className="mt-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded text-xs text-white transition-all"
                >
                  Download Template
                </button>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Select CSV File
            </label>
            <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-all">
              <Upload className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="domain-file-upload"
                disabled={uploading}
              />
              <label
                htmlFor="domain-file-upload"
                className="cursor-pointer text-sm text-slate-400 hover:text-slate-300"
              >
                {file ? (
                  <div className="text-cyan-400 font-medium">{file.name}</div>
                ) : (
                  <>
                    <span className="text-cyan-400 font-medium">Click to upload</span>
                    <span> or drag and drop</span>
                    <div className="text-xs text-slate-500 mt-1">CSV files only (.csv) — max 10MB</div>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="mb-6 p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Upload Results
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Total Rows:</span>
                  <span className="ml-2 text-slate-200 font-medium">{results.total}</span>
                </div>
                <div>
                  <span className="text-slate-500">Inserted:</span>
                  <span className="ml-2 text-green-400 font-medium">{results.success}</span>
                </div>
                <div>
                  <span className="text-slate-500">Duplicates (skipped):</span>
                  <span className="ml-2 text-yellow-400 font-medium">{results.duplicates}</span>
                </div>
                <div>
                  <span className="text-slate-500">Failed:</span>
                  <span className="ml-2 text-red-400 font-medium">{results.failed}</span>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="mt-4 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-slate-400 mb-2">Errors:</p>
                  {results.errors.map((err, idx) => (
                    <div key={idx} className="text-xs text-red-300 mb-1">
                      Row {err.row}: {err.error} {err.domain && `(${err.domain})`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Column Guide */}
          <div className="mb-4 p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">Required Column:</h3>
            <ul className="text-xs text-slate-400 space-y-1">
              <li><code className="text-cyan-400">domain</code> — The impersonating domain name (e.g., glareindustriess.com)</li>
            </ul>
            <h3 className="text-sm font-semibold text-slate-200 mt-3 mb-2">Optional Columns:</h3>
            <ul className="text-xs text-slate-400 space-y-1">
              <li><code className="text-cyan-400">type</code> — Typosquatting, Homograph Attack, Combosquatting, Brand Jacking, Phishing, Look-alike Domain, Other</li>
              <li><code className="text-cyan-400">status</code> — Active, Monitoring, Takedown Requested, Takedown Successful, Resolved, False Positive</li>
              <li><code className="text-cyan-400">risk</code> — Critical, High, Medium, Low, Informational</li>
              <li><code className="text-cyan-400">registered</code> — Registration date (YYYY-MM-DD)</li>
              <li><code className="text-cyan-400">registrar</code> — Domain registrar (e.g., Namecheap, GoDaddy)</li>
              <li><code className="text-cyan-400">similarity</code> — Phishing score (numeric, e.g., 95)</li>
              <li><code className="text-cyan-400">ip_address</code> — Resolved IP address</li>
              <li><code className="text-cyan-400">notes</code> — Additional details or context</li>
            </ul>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            onClick={resetAndClose}
            disabled={uploading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-sm text-slate-200 transition-all"
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm text-white transition-all flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload CSV
                </>
              )}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default BulkUploadDomainsModal;